import { defineStore } from "pinia";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import  DataOperationError  from "../models/api/dataOperationError";
import { ICenter } from "../models/center";
import { IParameter } from "../models/parameter";
import { IParameterForm } from "../models/forms/parameterForm";
import { IParameterFilter } from "../models/filters/parameterFilter";
import ParameterServices from "../services/ParameterServices";
import { API_CENTRE } from "../services/config";
import { useStartupStore } from "./startup.store";
import signalRManager from "../helper/signalRManager";
import i18n from "@/i18n";
import IPayloadStore from "../models/api/payloadStore";
import { compareByParameterOrder } from "../helper/parameterOrder";

export const useParameterStore = defineStore("newparameters",{
    state: () => ({
        datas: [] as Array<IParameter>,
        current : {} as IParameter,
        itemForm : {} as IParameterForm
    }),
    getters: {
        /**
         * Récupère un paramètre par son identifiant dans le store, sans passer par l'api
         * @param state 
         * @returns 
         */
        getParameterById: (state) => (id: number):IParameter|undefined => {
            return state.datas.find((item) => item.id == id);
        },
        /**
         * Récupère une liste de paramètres par leur code1 dans le store, sans passer par l'api
         * @param state 
         * @returns 
         */
        getParameterListByCode1: (state) => (code1: string): IParameter[]|undefined => {
            return state.datas.filter((item) => code1 != '' && item.code1 == code1);
        },

        /**
         * Récupére un parametre par son code dans le store, sans passer par l'api
         * @param state 
         * @returns 
         */
        getParameterByCodePair: (state) => (code1: string, code2: string): IParameter[]|undefined => {
            return state.datas.filter((item) => code1 != '' && code2 != '' && item.code1 == code1 && item.code2 == code2);
        },

        /**
         * Récupére un parametre par son code dans le store
         * @param state 
         * @returns 
         */
        getParameterByCodePairActif: (state) => (code1: string, code2: string): IParameter[]|undefined => {
            return state.datas.filter((item) => code1 != '' && code2 != '' && item.code1 == code1 && item.code2 == code2 && item.isActive == true)?.sort(compareByParameterOrder) || [];
        },

        /**
         * Récupère un paramètre par son code dans le store, sans passer par l'api
         * @param state 
         * @returns 
         */
        getParameterByCode3: (state) => (code1: string, code2: string,code3: string): IParameter|undefined => {
            return state.datas.find((item) =>code1 != '' && code2 != '' && item.code1 == code1 && item.code2 == code2 && code3 != '' && item.code3 == code3);
        },

        getParametersByCode3Including: (state) => (search: string): IParameter[] => {
            return state.datas.filter((item) => item.code3?.toUpperCase().includes(search.toUpperCase()));
        },

        /**
         * 
         * @param state 
         * @returns 
         */
        getParameterByFamille: (state) => ():IParameter[]|undefined => {
            return state.datas.filter(x => x.code1 == "PARAMETER" && x.code2 == "FAMILLE").sort((x, y) => Number(y.active) - Number(x.active));
        },
        getParameterByCritere: (state) => (sousCode : string):IParameter[]|undefined => {
            return state.datas.filter(x => x.code1 == sousCode && x.code2 == "CRITERE").sort((x, y) => Number(y.active) - Number(x.active))
        },
        // Grille de "Gestion des paramètres" (ParameterListView) : triée par order (0027758 —
        // un ordre absent passe en dernier, pas en tête), pour que la colonne "Ordre" et les
        // boutons monter/descendre reflètent réellement le rang affiché.
        getParameterByCodeSousCode: (state) => (code : string, sousCode : string):IParameter[]|undefined => {
            return state.datas.filter(x => x.code1 == code && x.code2 == sousCode).sort(compareByParameterOrder)
        },
    },
    actions: {
        
        /**
         * Chargement des données dans le store
         * @param filter Le filtre à appliquer pour le chargement des données
         * @returns 
         */
        async load(filter: IParameterFilter, api: string = API_CENTRE) {
            const startupStore = useStartupStore();
            ParameterServices.switchAPI(api);
            let result = await ParameterServices.getAll<IParameterFilter,IParameter>(filter);
            this.datas = [];
            if (!result.error) {
                this.datas = result.data as IParameter[];
            }
            return result;
        },


        /**
         * Récupération d'un item par son identifiant
         * @param id 
         * @returns 
         */
        async get(id: number, api: string = API_CENTRE) {
            ParameterServices.switchAPI(api);

            let result = await ParameterServices.getById<IParameter>(id);
            // mettre à jour dans le store ?
            if ( !result.error) {
                let index = this.datas.findIndex((a) => a.id == id);
                if (index >= 0)
                    this.datas[index] = result.data as IParameter;
                // on le stock dans le current puis dans le form
                this.current = result.data as IParameter;
                this.itemForm = await this.toForm(result.data as IParameter);
            }
            return result;
        },

        /**
         * Récupère un paramètre par son identifiant dans le store, sans passer par l'api
         * @param state 
         * @returns 
         */
        async searchStoreById(id: number, api: string = API_CENTRE) {
            ParameterServices.switchAPI(api);

            this.current = this.datas.find((parameter) => parameter.id == id)!;
            this.itemForm = await this.toForm(this.current as IParameter);
            return this.current;
        },

        /* A VERIFIER - SUREMENT OBSOLETE */
        async saveViaApi (maj:boolean = true, api: string = API_CENTRE)
        {
            ParameterServices.switchAPI(api);
            return this.save(maj);
        },

        async save(maj:boolean = true, isPatch:boolean = false)
        {
        
            let result = {} as ServiceOperationResult<IParameter|IDataOperationResult<IParameter>>;  
            if ( this.itemForm.id > 0) {
                result = await ParameterServices.update(this.itemForm);
                
                if ( !result.error)
                {
                    this.current = result.data as IParameter;
                    if ( maj ) {
                        let index = this.datas.findIndex((a) => a.id == this.itemForm.id);
                        if (index >= 0) {
                            this.datas[index] = this.current;
                        } else {
                            const error = new DataOperationError();
                            error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                            return new ServiceOperationResult<IDataOperationResult<IParameter>>(true, {errors: error});
                        }
                    }
                }
            } else {
                result = await ParameterServices.create(this.itemForm);
                if ( !result.error)
                {
                    this.current = result.data as IParameter;
                    // SignalR peut avoir déjà ajouté l'item avant la réponse HTTP (race condition)
                    if (this.datas.findIndex((a) => a.id == this.current.id) == -1) {
                        this.datas.push(this.current);
                    }
                }

            }
            return result;
        },

        /**
         * Échange l'ordre de deux paramètres (boutons monter/descendre de la grille). Réutilise
         * le PUT existant via toForm : chaque écriture republie l'event de synchro côté back
         * (ParameterController), donc les autres centres convergent sans code spécifique ici.
         */
        async swapOrder(a: IParameter, b: IParameter) {
            const orderA = a.order;
            const formA = this.toForm(a);
            formA.order = b.order;
            const formB = this.toForm(b);
            formB.order = orderA;

            const [resultA, resultB] = await Promise.all([
                ParameterServices.update(formA),
                ParameterServices.update(formB)
            ]);

            if (!resultA.error) {
                const updatedA = resultA.data as IParameter;
                const idx = this.datas.findIndex((x) => x.id == updatedA.id);
                if (idx >= 0) this.datas[idx] = updatedA;
            }
            if (!resultB.error) {
                const updatedB = resultB.data as IParameter;
                const idx = this.datas.findIndex((x) => x.id == updatedB.id);
                if (idx >= 0) this.datas[idx] = updatedB;
            }

            return { resultA, resultB };
        },

        toForm(item: IParameter):IParameterForm {
            const form = {} as IParameterForm;
            Object.assign(form, item);
            form.speciesId = item.species?.id;
            return form;
        },
        // permet d'initialiser un objet vide pour le formulaire
        newForm(lienId:number):IParameterForm {
            this.itemForm = {
                id: 0,
                code1: undefined,
                code2: undefined,
                code3: undefined,
                value: undefined,
                speciesId: undefined,
                active: true,
                order: undefined
            } as IParameterForm;
            return this.itemForm;
        },
        // permet d'initialiser un objet vide pour les referentiels
        newItem(lien:ICenter):IParameter {
            this.current = {
                id: 0,
                code1: undefined,
                code2: undefined,
                code3: undefined,
                value: undefined,
                species: (lien ? lien.species : undefined),
                active: true,
                order: undefined
            } as IParameter
            return this.current;
        }
    }
});

signalRManager.connection.on("MajParameter", (payload: IPayloadStore) => {
    const store = useParameterStore();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                store.datas.push(payload.data as IParameter);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                store.datas.splice(idx, 1, payload.data as IParameter);
            }
            // Les associations centre<->paramètre portent une référence figée au chargement :
            // remplacer l'objet ici ne suffit pas, il faut la repointer. C'est fait par
            // centerParameter.store, qui écoute le même message (l'abonnement y vit pour ne pas
            // créer de cycle d'import entre les deux stores).
            break;
        }
    }
});